import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Tour {
  id: string;
  title: string;
  slug: string;
  short_description: string | null;
  main_image_url: string | null;
  adult_price: number | null;
  duration: string | null;
}

const FeaturedToursSection = () => {
  const [tours, setTours] = useState<Tour[]>([]);

  useEffect(() => {
    supabase
      .from("tours")
      .select("id, title, slug, short_description, main_image_url, adult_price, duration")
      .eq("is_active", true)
      .eq("is_featured", true)
      .order("sort_order")
      .limit(6)
      .then(({ data }) => {
        if (data) setTours(data);
      });
  }, []);

  if (tours.length === 0) return null;

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <span className="text-secondary font-semibold text-sm uppercase tracking-widest">Explore</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mt-2">
            Popular Tours
          </h2>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
            Discover the most exciting experiences the Dominican Republic has to offer
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tours.map((tour, i) => (
            <motion.div
              key={tour.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <Link
                to={`/tours/${tour.slug}`}
                className="group block bg-white border border-border/50 rounded-none overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={tour.main_image_url || "/placeholder.svg"}
                    alt={tour.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {tour.adult_price && (
                    <div className="absolute top-3 right-3 bg-secondary text-secondary-foreground font-bold text-sm px-3 py-1 rounded-none">
                      ${tour.adult_price}
                    </div>
                  )}
                  {tour.duration && (
                    <div className="absolute bottom-3 left-3 bg-foreground/70 text-white text-xs font-medium px-2 py-1 rounded-none flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {tour.duration}
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-display font-bold text-foreground text-lg mb-2 group-hover:text-secondary transition-colors">
                    {tour.title}
                  </h3>
                  {tour.short_description && (
                    <p className="text-muted-foreground text-sm line-clamp-2 mb-3">
                      {tour.short_description}
                    </p>
                  )}
                  <span className="text-secondary text-sm font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    View Details <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/tours"
            className="inline-flex items-center gap-2 text-secondary font-semibold hover:gap-3 transition-all"
          >
            View All Tours <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedToursSection;
